import type { Persona } from './persona.service';

/**
 * "Recently used on this machine" for the debug wrapper's account picker.
 *
 * Stored in localStorage, so it is per debug host (per origin): debug.toybank.localhost and
 * debug.demo.localhost each keep their own list. A cookie would have the same per-host scope,
 * would ride every request to the BFF and is never needed by the server, so it is not used.
 *
 * Holds usernames only (the same names the picker already shows), most recent first, at most
 * {@link RECENT_ACCOUNTS_CAP}. Never a token, subject or label. Every storage access is wrapped:
 * a private window, blocked site data or a full quota simply means no recent group.
 */
export const RECENT_ACCOUNTS_KEY = 'tb-debug-recent-accounts';
export const RECENT_ACCOUNTS_CAP = 5;

/**
 * Upper bound on what a READ keeps. The cap of 5 applies on write and after the roster filter in
 * {@link groupPickerAccounts}, so a stale name ahead of five valid ones cannot push a valid one
 * out. This bound only stops an edited entry from costing anything.
 */
const READ_BOUND = 50;

const USERNAME = /^[a-z][a-z0-9-]{1,31}$/;

interface StoredRecentAccounts {
  v: 1;
  usernames: string[];
}

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

function defaultStorage(): StorageLike | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

function clean(values: unknown, cap: number): string[] {
  if (!Array.isArray(values)) return [];
  const out: string[] = [];
  for (const value of values) {
    if (typeof value !== 'string') continue;
    const username = value.trim().toLowerCase();
    if (USERNAME.test(username) && !out.includes(username)) out.push(username);
    if (out.length === cap) break;
  }
  return out;
}

/**
 * The stored usernames, most recent first. Anything unreadable or malformed reads as empty. Not
 * cut to {@link RECENT_ACCOUNTS_CAP}: names no longer on the roster are dropped before the cap.
 */
export function readRecentAccounts(storage: StorageLike | null = defaultStorage()): string[] {
  if (!storage) return [];
  try {
    const raw = storage.getItem(RECENT_ACCOUNTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Partial<StoredRecentAccounts> | null;
    return parsed && parsed.v === 1 ? clean(parsed.usernames, READ_BOUND) : [];
  } catch {
    return [];
  }
}

/**
 * Put {@code username} first after a successful debug sign-in and return the new list. Names not
 * in {@code known} (the current picker roster) are dropped on the way, so a retired account ages
 * out instead of holding a slot. Returns the list unchanged when the name is not a username.
 */
export function recordRecentAccount(
  username: string,
  known: readonly string[] | null = null,
  storage: StorageLike | null = defaultStorage(),
): string[] {
  const current = readRecentAccounts(storage);
  const name = typeof username === 'string' ? username.trim().toLowerCase() : '';
  if (!USERNAME.test(name)) return current;
  const knownSet = known ? new Set(known.map((k) => k.toLowerCase())) : null;
  const next = clean(
    [name, ...current.filter((u) => u !== name && (!knownSet || knownSet.has(u)))],
    RECENT_ACCOUNTS_CAP,
  );
  if (storage) {
    try {
      const value: StoredRecentAccounts = { v: 1, usernames: next };
      storage.setItem(RECENT_ACCOUNTS_KEY, JSON.stringify(value));
    } catch {
      /* storage may be unavailable or full: the picker still works without a recent group */
    }
  }
  return next;
}

export type PickerAudience = 'customer' | 'staff';

export interface PickerGroups {
  /** Recently used accounts still on the roster, most recent first, at most the cap. */
  recent: Persona[];
  /** Everyone else, in roster order, split by audience. */
  customers: Persona[];
  staff: Persona[];
  /** Accounts nobody has an audience for. Never guessed into customers or staff. */
  other: Persona[];
}

/**
 * Split the roster for the picker. A recent account appears ONLY in {@code recent} (never twice:
 * every row's testid must stay unique). Stale recent usernames (not on the roster) are dropped
 * BEFORE the cap, so they never cost a valid account its place. {@code audienceOf} returns null
 * for an unknown audience, which files the account under {@code other}.
 */
export function groupPickerAccounts(
  personas: readonly Persona[],
  recentUsernames: readonly string[],
  audienceOf: (persona: Persona) => PickerAudience | null = audienceOfPersona,
): PickerGroups {
  const byUsername = new Map(personas.map((p) => [p.username.toLowerCase(), p]));
  const recent: Persona[] = [];
  for (const username of recentUsernames) {
    if (recent.length === RECENT_ACCOUNTS_CAP) break;
    const persona = byUsername.get(username.toLowerCase());
    if (persona && !recent.includes(persona)) recent.push(persona);
  }
  const rest = personas.filter((p) => !recent.includes(p));
  const audiences = new Map(rest.map((p) => [p, audienceOf(p)] as const));
  return {
    recent,
    customers: rest.filter((p) => audiences.get(p) === 'customer'),
    staff: rest.filter((p) => audiences.get(p) === 'staff'),
    other: rest.filter((p) => audiences.get(p) == null),
  };
}

/** The audience /bff/debug/users carries (from the test-account catalog), else the roles. */
export function audienceOfPersona(persona: Persona): PickerAudience | null {
  const audience = persona.audience;
  return audience === 'customer' || audience === 'staff' ? audience : audienceFromRoles(persona);
}

/**
 * Realm roles that say nothing about the audience. An allow-listed user with no
 * {@code user-profiles} entry is served with roles ["user"] only.
 */
const GENERIC_ROLES = new Set(['user', 'offline_access', 'uma_authorization']);

/**
 * Last-resort audience from roles: customer with the {@code customer} role, staff with any other
 * specific role, null (unknown) when there are only generic roles.
 */
export function audienceFromRoles(persona: Persona): PickerAudience | null {
  const roles = (persona.roles ?? []).map((r) => r.toLowerCase());
  if (roles.includes('customer')) return 'customer';
  return roles.some((r) => !GENERIC_ROLES.has(r) && !r.startsWith('default-roles-')) ? 'staff' : null;
}

/** "Nadia - ACME Inc. (company admin)" -> name "Nadia", detail "ACME Inc. (company admin)". */
export function splitAccountLabel(label: string): { name: string; detail: string | null } {
  const text = (label ?? '').trim();
  const at = text.indexOf(' - ');
  if (at <= 0) return { name: text, detail: null };
  return { name: text.slice(0, at), detail: text.slice(at + 3).trim() || null };
}
